import { useTranslation } from 'react-i18next';

/**
 * Section label per route — the page-layout template's `dynamicTextByRoute`
 * (first `pathStartsWith` match wins; default "Stats"). Shared by the TopBar
 * breadcrumb and the Lumio panel's section tag so the two can't drift apart.
 *
 * The `text` values stay English keys (also used as the stable section id that
 * `useLumioChat` matches against); translate them reactively at the render site
 * via `useRouteLabel`, never at module scope.
 */
export const ROUTE_LABELS: ReadonlyArray<{ pathStartsWith: string; text: string }> = [
  { pathStartsWith: '/account/home', text: 'Home' },
  { pathStartsWith: '/account/product', text: 'Product' },
  // Marketing (`/account/marketing-assets`) MUST precede Acquisition (`/account/marketing`): the match is a
  // plain prefix, so the Acquisition row would otherwise claim the Marketing pages.
  { pathStartsWith: '/account/marketing-assets', text: 'Marketing' },
  { pathStartsWith: '/account/marketing', text: 'Acquisition' },
  { pathStartsWith: '/account/operations', text: 'Operations' },
  { pathStartsWith: '/account/website', text: 'Website' },
  { pathStartsWith: '/account/players', text: 'Players' },
  { pathStartsWith: '/account/pulse', text: 'Pulse' },
  // The Stats page's Website analytics tab (`/account/stats/website`) MUST precede Stats, for the same prefix reason.
  { pathStartsWith: '/account/stats/website', text: 'Website Analytics' },
  { pathStartsWith: '/account/stats', text: 'Stats' },
  { pathStartsWith: '/account/wallet', text: 'Wallet' },
  { pathStartsWith: '/account/settings', text: 'Settings' },
  { pathStartsWith: '/account/onboarding', text: 'Customizer' },
];

const matchRoute = (pathname: string): (typeof ROUTE_LABELS)[number] | undefined =>
  ROUTE_LABELS.find((entry) => pathname.startsWith(entry.pathStartsWith));

export const routeLabel = (pathname: string): string => matchRoute(pathname)?.text ?? 'Stats';

/**
 * The area-root URL for a path — the matched section's `pathStartsWith`, which IS
 * that area's root (`/account/marketing` for an Acquisition campaign detail, etc.).
 * Derived from the same lookup as `routeLabel` so the breadcrumb's label and its
 * link target can't drift apart. Falls back to the Stats root to mirror the
 * `routeLabel` default.
 */
export const routeAreaRoot = (pathname: string): string =>
  matchRoute(pathname)?.pathStartsWith ?? '/account/stats';

/**
 * Reactive, translated section label for a route. Keeps the English `routeLabel`
 * as a stable id and maps it through `t()` at the render site (explicit literals
 * so the extractor harvests this closed UI vocabulary). Re-evaluates on language
 * change. Returns the raw English label for any unmapped value (defensive only).
 */
export const useRouteLabel = (pathname: string): string => {
  const { t } = useTranslation();
  const label = routeLabel(pathname);
  const labels: Record<string, string> = {
    Home: t('Home'),
    Product: t('Product'),
    Marketing: t('Marketing'),
    Acquisition: t('Acquisition'),
    Operations: t('Operations'),
    Website: t('Website'),
    Players: t('Players'),
    Pulse: t('Pulse'),
    Stats: t('Stats'),
    'Website Analytics': t('Website Analytics'),
    Wallet: t('Wallet'),
    Settings: t('Settings'),
    Customizer: t('Customizer'),
  };
  return labels[label] ?? label;
};
